import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberOrchestrator } from "@nju/data-access-portal-member";
import { IOfferModeWithFSecure } from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { pushSimpleEvent } from "@nju/data-layer";
import { portalRoutes } from "@nju/routes";
import { GeneralErrorNotification } from "@nju/ui";
import { cancelOrderMachine } from "./cancelOrder.machine";
import { CancelOrderError } from "./partials/CancelOrderError";
import { CancelOrderForm } from "./partials/CancelOrderForm";
import { resolveOrchestrator } from "./utils";

interface IDashboardCancelOrderPageProps {
  mode: IOfferModeWithFSecure;
}

export function DashboardCancelOrderPage({
  mode,
}: IDashboardCancelOrderPageProps) {
  const router = useRouter();
  const params = portalRoutes.mojeKonto.anulujZamowienie
    .input(router.query)
    .unwrap();
  const ftthOrchestrator = useOrchestrators("ftth", { isOptional: true });
  const fsecureOrchestrator = useOrchestrators("fsecure", { isOptional: true });
  const multiOrderOrchestrator = useOrchestrators("multiOrder", {
    isOptional: true,
  });
  const memberOrchestrators = useMemberOrchestrator();
  const selectedOrchestrator = resolveOrchestrator({
    mode,
    ftth: ftthOrchestrator,
    fsecure: fsecureOrchestrator,
    mobile: params?.userId
      ? memberOrchestrators?.mobile
      : multiOrderOrchestrator,
  });

  const [current, send] = useMachine(cancelOrderMachine, {
    actions: {
      redirectToDashboard: () => {
        router.push(
          portalRoutes.mojeKonto.index.get({
            mode,
            ...(selectedOrchestrator?.type === "NJU_MULTIORDER_ONBOARDING" &&
              params?.flow && { flow: params?.flow }),
          }),
        );
      },
    },
    context: {
      orchestrator: selectedOrchestrator,
      mode,
    },
  });

  const { matches } = current;

  const submit = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "service_resignation_complete_confirm",
    });
    return send({ type: "SUBMIT" });
  }, [send]);

  if (matches("error")) {
    return <CancelOrderError mode={mode} />;
  }

  if (!selectedOrchestrator) {
    return (
      <GeneralErrorNotification
        callback={() => {
          router.push(portalRoutes.mojeKonto.index.get({ mode }));
        }}
      />
    );
  }

  return (
    <CancelOrderForm
      isLoading={matches("send")}
      mode={mode}
      processId={selectedOrchestrator?.processId}
      submit={submit}
    />
  );
}
